import { React, useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Card,
  CardContent,
  FormControl,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Radio,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import CalculateIcon from "@mui/icons-material/Calculate";
import ConfirmationDialog from "components/ConfirmationDialog";
import SnackbarWrapper from "components/Snackbar";
import { addManpowerRequirement } from "services/api";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { getManpowerDashboardData } from "services/api";
import { useAuth } from "context/authContext";
import {
  addScenarioJob,
  deleteRevenueGeneratorsByJobId,
  deleteRevenueGeneratorsSimulationByJobId,
} from "services/api";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"];
const weekendDays = ["Friday", "Saturday"];
export default function HoursCalculator() {
  const navigate = useNavigate();
  const location = useLocation();
  const { job, allJobs, formData, isScenario, ratio, ratioSource, loc, scenario } =
    location?.state || {}; //Job selected and all other jobs
  const [selectedJob, setSelectedJob] = useState(job || null); //selected job
  const [contractualHours, setContractualHours] = useState(job?.contractualHoursPerWeek || ""); //contractual hours of selected jobs
  const { t, i18n } = useTranslation();
  const { employee } = useAuth();
  const [jobs, setJobs] = useState(allJobs || []);
  const [jobsLoading, setJobsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setJobsLoading(true);
      if (!allJobs) {
        const res = await getManpowerDashboardData(employee.jobId);
        if (res !== "error") {
          const sorted = [...res].sort((a, b) => a.jobId - b.jobId);
          setJobs(sorted);
        }
      } else {
        setJobs(allJobs);
      }
      setJobsLoading(false);
    };

    fetchData();
  }, [allJobs, employee.jobId]);

  const isArabic = useMemo(() => {
    return i18n.language === "ar";
  }, [i18n.language]);

  const [activeIndex, setActiveIndex] = useState(0); // which card is visible
  const [view, setView] = useState("work"); //view at different working hours radio button
  const [sameHoursView, setSameHoursView] = useState("work"); //view at same working hours radio button
  const [device, setDevice] = useState("desktop");

  //actual hours for each day (diff working hrs radio button)
  const [hoursData, setHoursData] = useState(
    days.reduce((acc, day) => {
      acc[day] = { technical: "", meetings: "", learning: "" };
      return acc;
    }, {})
  );
  //weekend hours for each day (diff working hrs radio button)
  const [weekendHoursData, setWeekendHoursData] = useState(
    weekendDays.reduce((acc, day) => {
      acc[day] = { technical: "", meetings: "", learning: "" };
      return acc;
    }, {})
  );

  //weekend hours for each day (same working hrs radio button)
  const [sameHoursWeekendData, setSameHoursWeekendData] = useState(
    weekendDays.reduce((acc, day) => {
      acc[day] = { technical: "", meetings: "", learning: "" };
      return acc;
    }, {})
  );

  // for tick button (diff working hrs radio button)
  const [noWorkingHours, setNoWorkingHours] = useState(
    weekendDays.reduce((acc, day) => {
      acc[day] = false; // initially false for each weekend day
      return acc;
    }, {})
  );

  // for tick button (same working hrs radio button)
  const [noSameWorkingHours, setNoSameWorkingHours] = useState(
    weekendDays.reduce((acc, day) => {
      acc[day] = false;
      return acc;
    }, {})
  );

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();

    if (/ipad/.test(ua) || (/macintosh/.test(ua) && "ontouchend" in document)) {
      // iPadOS 13+ identifies as Mac, but has touch
      setDevice("tablet");
    } else if (/mobi|android|iphone|ipod/.test(ua)) {
      setDevice("mobile");
    } else {
      setDevice("desktop");
    }
  }, []);

  //actual hours when same for all days
  const [hoursDataAllDays, setHoursDataAllDays] = useState({
    technical: "",
    meetings: "",
    learning: "",
  });

  const snackbarRef = useRef();

  const [sameWorkingHours, setSameWorkingHours] = useState(null); //does all days have same work hours (y/n)
  const startX = useRef(0); // where swipe started
  const currentX = useRef(0); // where finger/mouse currently is
  const isDragging = useRef(false); // are we dragging/swiping right now?
  const [dragging, setDragging] = useState(false); // used for animation control
  const [confOpen, setConfOpen] = useState(false);

  useEffect(() => {
    if (selectedJob) {
      setContractualHours(selectedJob.contractualHoursPerWeek || "");
    }
  }, [selectedJob]);

  //Runs when the user first touches or clicks
  const handleStart = (x) => {
    startX.current = x; //Save where the swipe/drag began (save position)
    currentX.current = x; //Initialize "current position" to the same value
    isDragging.current = true; //Mark that dragging is now in progress
    setDragging(true); //Update React state (used for animations)
  };

  //Runs repeatedly while the user is dragging/swiping
  //Updates current position while finger/mouse moves.
  const handleMove = (x) => {
    if (!isDragging.current) return;
    currentX.current = x;
  };

  //Runs when the user releases finger/mouse
  const handleEnd = () => {
    if (!isDragging.current) return;
    const diff = currentX.current - startX.current; //Calculate the horizontal distance moved.

    if (diff > 50) {
      // swipe right
      setActiveIndex(
        (prev) => (prev === 0 ? days.length - 1 : prev - 1) // if at Sunday → go to Thursday
      );
    } else if (diff < -50) {
      // swipe left
      setActiveIndex(
        (prev) => (prev === days.length - 1 ? 0 : prev + 1) // if at Thursday → go to Sunday
      );
    }

    isDragging.current = false;
    setDragging(false);
  };

  const handleSaveHours = async () => {
    let workingHoursPerWeek;

    if (sameWorkingHours === "no") {
      const { dayTotals, weekTotal, isEmptyFields } = calculateTotalsHoursEachDay();

      if (isEmptyFields || contractualHours === null || contractualHours === "") {
        snackbarRef.current?.showSnackbar(t("user.hoursCalculator.snackbar.missing"), "error");
        return;
      }
      workingHoursPerWeek = weekTotal;
    } else {
      const { total, isEmptyFields } = calculateTotalHours();

      if (isEmptyFields || contractualHours === null || contractualHours === "") {
        snackbarRef.current?.showSnackbar(t("user.hoursCalculator.snackbar.missing"), "error");

        return;
      }

      workingHoursPerWeek = total;
    }
    // const isRevenueGenerator = formData?.revenueGenerator === true;

    let commonFields = {
      revenueGenerator: formData?.revenueGenerator ?? false,
      minimumFTE: formData?.minimumFTE,
    };

    if (formData?.revenueGenerator === true) {
      // Case 1: Revenue Generator
      commonFields.companyTargetsPerYear =
        formData?.companyTargetsPerYear !== null &&
        formData?.companyTargetsPerYear !== undefined &&
        formData?.companyTargetsPerYear !== ""
          ? Number(formData.companyTargetsPerYear)
          : null;
      commonFields.contractualHoursPerWeek =
        contractualHours !== null && contractualHours !== undefined && contractualHours !== ""
          ? Number(contractualHours)
          : null;
      commonFields.actualHoursPerWeek =
        workingHoursPerWeek !== null &&
        workingHoursPerWeek !== undefined &&
        workingHoursPerWeek !== ""
          ? Number(workingHoursPerWeek)
          : null;
      commonFields.avgProductivityPerMonth =
        formData?.avgProductivityPerMonth !== null &&
        formData?.avgProductivityPerMonth !== undefined &&
        formData?.avgProductivityPerMonth !== ""
          ? Number(formData.avgProductivityPerMonth)
          : null;
      commonFields.ratio = null;
      commonFields.ratioName = null;
      commonFields.ratioQuantity = null;
      commonFields.dailyUtilizationHrs = null;
      commonFields.weeklyUtilizationDays = null;
      commonFields.dailyEmpWorkingHrs = null;
      commonFields.empWorkingDaysPerWeek = null;
      commonFields.vacationDays = null;

      commonFields.alternativeRatioName = null;
      commonFields.alternativeRatioQuantity = null;
      commonFields.alternativeRatioValue = null;
      commonFields.altDailyUtilizationHrs = null;
      commonFields.altWeeklyUtilizationDays = null;
      commonFields.altDailyEmpWorkingHrs = null;
      commonFields.altEmpWorkingDaysPerWeek = null;
      commonFields.altVacationDays = null;
      commonFields.isShifts = null;
      commonFields.isMultipleRatios = null;
    } else if (ratioSource === "yes") {
      // Case 2: Non-revenue generator with employee ratio = YES
      commonFields.actualHoursPerWeek =
        workingHoursPerWeek !== null &&
        workingHoursPerWeek !== undefined &&
        workingHoursPerWeek !== ""
          ? Number(workingHoursPerWeek)
          : null;
      commonFields.contractualHoursPerWeek =
        contractualHours !== null && contractualHours !== undefined && contractualHours !== ""
          ? Number(contractualHours)
          : null;
      commonFields.ratio = ratio != null && ratio !== "" ? Number(ratio) : null;
      commonFields.ratioName = "x*;_q*n!!e&m%p";
      commonFields.companyTargetsPerYear = null;
      commonFields.avgProductivityPerMonth = null;
      commonFields.ratioQuantity = null;
      commonFields.dailyUtilizationHrs = null;
      commonFields.weeklyUtilizationDays = null;
      commonFields.dailyEmpWorkingHrs = null;
      commonFields.empWorkingDaysPerWeek = null;
      commonFields.vacationDays = null;

      commonFields.alternativeRatioName = null;
      commonFields.alternativeRatioQuantity = null;
      commonFields.alternativeRatioValue = null;
      commonFields.altDailyUtilizationHrs = null;
      commonFields.altWeeklyUtilizationDays = null;
      commonFields.altDailyEmpWorkingHrs = null;
      commonFields.altEmpWorkingDaysPerWeek = null;
      commonFields.altVacationDays = null;
      commonFields.isShifts = null;
      commonFields.isMultipleRatios = null;

      if (!isScenario) {
        await deleteRevenueGeneratorsByJobId(job.jobId);
      } else {
        await deleteRevenueGeneratorsSimulationByJobId(job.id);
      }
    } else if (ratioSource === "no") {
      // Case 3: Non-revenue generator with employee ratio = NO
      commonFields.actualHoursPerWeek =
        workingHoursPerWeek !== null &&
        workingHoursPerWeek !== undefined &&
        workingHoursPerWeek !== ""
          ? Number(workingHoursPerWeek)
          : null;
      commonFields.contractualHoursPerWeek =
        contractualHours !== null && contractualHours !== undefined && contractualHours !== ""
          ? Number(contractualHours)
          : null;
      commonFields.ratio = ratio != null && ratio !== "" ? Number(ratio) : null;
      // commonFields.ratioName = formData?.ratioName ?? null;
      commonFields.ratioName =
        formData?.ratioName === "x*;_q*n!!e&m%p" ? null : (formData?.ratioName ?? null);
      commonFields.companyTargetsPerYear = null;
      commonFields.avgProductivityPerMonth = null;
      commonFields.ratioQuantity =
        formData?.ratioQuantity !== null &&
        formData?.ratioQuantity !== undefined &&
        formData?.ratioQuantity !== ""
          ? Number(formData.ratioQuantity)
          : null;
      commonFields.alternativeRatioQuantity =
        formData?.alternativeRatioQuantity !== null &&
        formData?.alternativeRatioQuantity !== undefined &&
        formData?.alternativeRatioQuantity !== ""
          ? Number(formData.alternativeRatioQuantity)
          : null;
      commonFields.empWorkingDaysPerWeek =
        formData?.empWorkingDaysPerWeek !== null &&
        formData?.empWorkingDaysPerWeek !== undefined &&
        formData?.empWorkingDaysPerWeek !== ""
          ? Number(formData.empWorkingDaysPerWeek)
          : null;
      commonFields.dailyUtilizationHrs =
        formData?.dailyUtilizationHrs !== null &&
        formData?.dailyUtilizationHrs !== undefined &&
        formData?.dailyUtilizationHrs !== ""
          ? Number(formData.dailyUtilizationHrs)
          : null;
      commonFields.weeklyUtilizationDays =
        formData?.weeklyUtilizationDays !== null &&
        formData?.weeklyUtilizationDays !== undefined &&
        formData?.weeklyUtilizationDays !== ""
          ? Number(formData.weeklyUtilizationDays)
          : null;
      commonFields.dailyEmpWorkingHrs =
        formData?.dailyEmpWorkingHrs !== null &&
        formData?.dailyEmpWorkingHrs !== undefined &&
        formData?.dailyEmpWorkingHrs !== ""
          ? Number(formData.dailyEmpWorkingHrs)
          : null;
      commonFields.vacationDays =
        formData?.vacationDays !== null &&
        formData?.vacationDays !== undefined &&
        formData?.vacationDays !== ""
          ? Number(formData.vacationDays)
          : null;
      commonFields.altEmpWorkingDaysPerWeek =
        formData?.altEmpWorkingDaysPerWeek !== null &&
        formData?.altEmpWorkingDaysPerWeek !== undefined &&
        formData?.altEmpWorkingDaysPerWeek !== ""
          ? Number(formData.altEmpWorkingDaysPerWeek)
          : null;
      commonFields.alternativeRatioValue =
        formData?.alternativeRatioValue != null && formData?.alternativeRatioValue !== ""
          ? Number(formData?.alternativeRatioValue)
          : null;
      commonFields.altDailyUtilizationHrs =
        formData?.altDailyUtilizationHrs !== null &&
        formData?.altDailyUtilizationHrs !== undefined &&
        formData?.altDailyUtilizationHrs !== ""
          ? Number(formData.altDailyUtilizationHrs)
          : null;
      commonFields.altWeeklyUtilizationDays =
        formData?.altWeeklyUtilizationDays !== null &&
        formData?.altWeeklyUtilizationDays !== undefined &&
        formData?.altWeeklyUtilizationDays !== ""
          ? Number(formData.altWeeklyUtilizationDays)
          : null;
      commonFields.altDailyEmpWorkingHrs =
        formData?.altDailyEmpWorkingHrs !== null &&
        formData?.altDailyEmpWorkingHrs !== undefined &&
        formData?.altDailyEmpWorkingHrs !== ""
          ? Number(formData.altDailyEmpWorkingHrs)
          : null;
      commonFields.altVacationDays =
        formData?.altVacationDays !== null &&
        formData?.altVacationDays !== undefined &&
        formData?.altVacationDays !== ""
          ? Number(formData.altVacationDays)
          : null;
      // commonFields.isShifts =
      //   formData?.isShifts !== null &&
      //   formData?.isShifts !== undefined &&
      //   formData?.isShifts !== ""
      //       ? formData.isShifts
      //       : null;

      // commonFields.isMultipleRatios =
      //   formData?.isMultipleRatios !== null &&
      //   formData?.isMultipleRatios !== undefined &&
      //   formData?.isMultipleRatios !== ""
      //       ? formData.isMultipleRatios
      //       : null;
      commonFields.alternativeRatioName =
        formData?.alternativeRatioName !== null &&
        formData?.alternativeRatioName !== undefined &&
        formData?.alternativeRatioName !== ""
          ? formData.alternativeRatioName
          : null;

      commonFields.isShifts = formData?.isShifts ?? null;
      commonFields.isMultipleRatios = formData?.isMultipleRatios ?? null;

      if (!isScenario) {
        await deleteRevenueGeneratorsByJobId(job.jobId);
      } else {
        await deleteRevenueGeneratorsSimulationByJobId(job.id);
      }
    } else {
      commonFields.actualHoursPerWeek =
        workingHoursPerWeek !== null &&
        workingHoursPerWeek !== undefined &&
        workingHoursPerWeek !== ""
          ? Number(workingHoursPerWeek)
          : null;
      commonFields.contractualHoursPerWeek =
        contractualHours !== null && contractualHours !== undefined && contractualHours !== ""
          ? Number(contractualHours)
          : null;
    }

    const payload = {
      ...selectedJob,
      ...commonFields,
    };

    const finalPayload = isScenario
      ? {
          ...payload,
          scenarioId: scenario?.id, // 👈 add only in scenario mode
        }
      : payload;

    try {
      // const response= await updateManpowerRequirement(selectedJob.requirementId, payload);
      const response = isScenario
        ? await addScenarioJob(finalPayload) // or jobData
        : await addManpowerRequirement(finalPayload);
      if (response !== "error") {
        // setContractualHours("");
        navigate(loc, { state: { view: loc === "/organization" ? 2 : 0, scenario } });

        // snackbarRef.current?.showSnackbar("Manpower requirement saved!", "success");
        snackbarRef.current?.showSnackbar(t("user.hoursCalculator.snackbar.add"), "success");
      }
    } catch (err) {}
  };

  //Calculate Total Working Hours For each day(Different Working Hours)
  const calculateTotalsHoursEachDay = () => {
    let weekTotal = 0;
    const dayTotals = {};
    let isEmptyFields = false;

    // Combine weekdays and weekends for iteration
    const allDays = [...days, ...weekendDays];

    // for (const day of days) {
    for (const day of allDays) {
      // const { technical, meetings, learning } = hoursData[day];
      const data = days.includes(day) ? hoursData[day] : weekendHoursData[day];
      const { technical, meetings, learning } = data;

      // Check if any field is empty
      if (technical === "" || meetings === "" || learning === "") {
        isEmptyFields = true;
        return { dayTotals: {}, weekTotal: 0, isEmptyFields }; // stop immediately
      }

      const total = Number(technical) + Number(meetings) + Number(learning);

      dayTotals[day] = total;
      weekTotal += total;
    }

    return { dayTotals, weekTotal, isEmptyFields };
  };

  //Calculate Total Working Hours For A day(Same Working Hours)
  const calculateTotalHours = () => {
    const { technical, meetings, learning } = hoursDataAllDays;
    let isEmptyFields = false;

    // Check if any field is empty
    if (technical === "" || meetings === "" || learning === "") {
      isEmptyFields = true;
      return { total: 0, isEmptyFields }; // stop immediately
    }

    let total = (Number(technical) + Number(meetings) + Number(learning)) * 5;

    weekendDays.forEach((day) => {
      const dayData = sameHoursWeekendData[day];
      if (!dayData) return;

      // If any weekend field is empty, mark isEmptyFields
      if (dayData.technical === "" || dayData.meetings === "" || dayData.learning === "") {
        isEmptyFields = true;
      }

      total +=
        Number(dayData.technical || 0) +
        Number(dayData.meetings || 0) +
        Number(dayData.learning || 0);
    });

    return { total, isEmptyFields };
  };

  return (
    <Box p={{ xs: 1, sm: 2, md: 3 }}>
      <Typography variant="h4" gutterBottom>
        {/* Hours Calculator */}
        {t("user.hoursCalculator.title")}
      </Typography>
      {jobs ? (
        <>
          <Box mt={3}>
            {/* Job Selection */}
            <Autocomplete
              size="small"
              options={jobs}
              getOptionLabel={(option) => option.jobName || ""}
              value={selectedJob}
              onChange={(e, newValue) => {
                setSelectedJob(newValue); // update selected job
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // label="Select Job"
                  label={t("user.hoursCalculator.selectJob")}
                  variant="outlined"
                  // sx={{ width: 350 }}
                  sx={{
                    width: { xs: "100%", sm: "350px" }, // full width on mobile
                  }}
                />
              )}
              isOptionEqualToValue={
                isScenario
                  ? (option, value) => option.id === value.id
                  : (option, value) => option.jobId === value.jobId
              }
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
            {/* Label */}
            <Box flex={{ xs: "1 1 100%", sm: "0 0 auto" }}>
              <Typography variant="body2" sx={{ whiteSpace: { xs: "normal", sm: "nowrap" } }}>
                {/* Contractual Hours: */}
                {t("user.hoursCalculator.contractualHrs")}
              </Typography>
            </Box>

            {/* Input */}
            <Box flex={{ xs: "1 1 100%", sm: "0 0 auto" }}>
              <TextField
                type="number"
                size="small"
                // placeholder="Enter hours"
                placeholder={t("user.hoursCalculator.enterHrs")}
                fullWidth
                value={contractualHours}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || Number(value) > 0) {
                    setContractualHours(value);
                  }
                }}
                inputProps={{ min: 1 }}
                sx={{
                  width: { xs: "100%", sm: 200, md: 200 }, // fixed max size
                }}
              />
            </Box>
            
          </Box>

          {/* Actual Hours Radio Button */}

          <Grid container mt={2}>
            {/* Label column */}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t("user.hoursCalculator.actualHrs")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {/* <Grid item xs={12} sm={9} md={8} mt={3} > */}
              <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>
                {/* Do all the days have the same working hours? */}
                {t("user.hoursCalculator.hrsQues")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl>
                <RadioGroup
                  row
                  value={sameWorkingHours}
                  onChange={(e) => setSameWorkingHours(e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        size="small"
                        icon={
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid #1976d2",
                              display: "inline-block",
                            }}
                          />
                        }
                        checkedIcon={
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid #1976d2",
                              display: "inline-block",
                              backgroundColor: "#1976d2",
                            }}
                          />
                        }
                      />
                    }
                    label={
                      <Typography variant="caption">
                        {t("user.hoursCalculator.hrsAnswerYes")}
                      </Typography>
                    }
                  />

                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        size="small"
                        icon={
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid #1976d2",
                              display: "inline-block",
                            }}
                          />
                        }
                        checkedIcon={
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid #1976d2",
                              display: "inline-block",
                              backgroundColor: "#1976d2",
                            }}
                          />
                        }
                      />
                    }
                    label={
                      <Typography variant="caption">
                        {t("user.hoursCalculator.hrsAnswerNo")}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
              {/* </Grid> */}
            </Grid>
          </Grid>

          {/* Different Working Hours Carousel */}

          {sameWorkingHours === "no" && (
            <>
              
              {view === "work" && (
                <>
                  {/* ✅ NEW: Days Completed List — only shows in work view */}
                  <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} mb={2}>
                    {days
                      .filter((day) => Object.values(hoursData[day]).every((v) => v !== ""))
                      .map((day) => (
                        <Typography
                          key={day}
                          variant="body2"
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: "rgba(0, 255, 0, 0.08)", // ✅ soft green highlight
                            border: "1px solid green",
                            fontWeight: 500,
                          }}
                        >
                          {/* {day} */}
                          {t(`user.hoursCalculator.days.${day}`)}{" "}
                        </Typography>
                      ))}
                  </Box>

                  <Box
                    sx={{ width: "100%" }} // ✅ modified: outer box takes full width
                    // ✅ modified: swipe events moved to this outer full-width zone
                    onTouchStart={(e) => handleStart(e.touches[0].clientX)}
                    onTouchMove={(e) => handleMove(e.touches[0].clientX)}
                    onTouchEnd={handleEnd}
                    onMouseDown={(e) => handleStart(e.clientX)}
                    onMouseMove={(e) => handleMove(e.clientX)}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}
                  >
                    <Box display="flex" justifyContent="center" mt={0}>
                      <Box
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          width: "100%",
                          maxWidth: 450,
                          maxWidth: { xs: "100%", sm: 500, md: 450 },
                          minHeight: 310,
                          borderRadius: 1,
                          margin: "0 auto",
                          boxShadow: 3,
                          touchAction: "pan-y",
                        }}
                      >
                        {/* Cards */}
                        {days.map((day, index) => {
                          const allFilledForDay = Object.values(hoursData[day]).every(
                            (v) => v !== ""
                          );
                          const allDaysFilled = days.every((day) =>
                            Object.values(hoursData[day]).every((v) => v !== "")
                          );

                          return (
                            <Card
                              key={day}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                // maxWidth: 450,
                                flex: "0 0 100%",
                                //index=card's num, activeIndex=card currently viewing
                                transform: `translateX(${(index - activeIndex) * 100}%)`, //transform shifts it left/right based on activeIndex.
                                transition: dragging ? "none" : "transform 0.3s ease",
                                // p: 3,
                                p: { xs: 2, sm: 3 },
                                boxShadow: 3,
                                borderRadius: 2,
                                border: allFilledForDay
                                  ? "2px solid green"
                                  : "2px solid transparent",
                                backgroundColor: allFilledForDay
                                  ? "rgba(0, 255, 0, 0.04)"
                                  : "white",
                              }}
                            >
                              {allFilledForDay && (
                                <CheckCircleIcon
                                  sx={{
                                    position: "absolute",
                                    top: 30,
                                    right: 10,
                                    color: "green",
                                    fontSize: 28,
                                  }}
                                />
                              )}

                              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                                {/* {day} */}
                                {t(`user.hoursCalculator.days.${day}`)}
                              </Typography>

                              {[
                                {
                                  type: "technical",
                                  labelKey: "user.hoursCalculator.technical",
                                  descKey: "user.hoursCalculator.technicalDescription",
                                },
                                {
                                  type: "meetings",
                                  labelKey: "user.hoursCalculator.meetings",
                                  descKey: "user.hoursCalculator.meetingsDescription",
                                },
                                {
                                  type: "learning",
                                  labelKey: "user.hoursCalculator.learningAndDev",
                                  descKey: "user.hoursCalculator.learningAndDevDescription",
                                },
                              ].map(({ type, labelKey, descKey }) => (
                                <Box
                                  key={type}
                                  display="flex"
                                  gap={4}
                                  mb={2}
                                  alignItems="flex-start"
                                >
                                  {/* Label and description */}
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    sx={{ width: 180, ml: 1 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      // sx={{ whiteSpace: "nowrap" }}
                                      sx={{
                                        width: { xs: "100%", sm: 120, md: 160 },
                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                      }}
                                    >
                                      {/* {label} */}
                                      {t(labelKey)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {/* {description} */}
                                      {t(descKey)}
                                    </Typography>
                                  </Box>

                                  {/* Input field */}
                                  <TextField
                                    type="number"
                                    size="small"
                                    // placeholder="Enter hours"
                                    placeholder={t("user.hoursCalculator.enterHrs")}
                                    value={hoursData[day][type]}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === "" || Number(value) >= 0) {
                                        setHoursData((prev) => ({
                                          ...prev,
                                          [day]: { ...prev[day], [type]: value },
                                        }));
                                      }
                                    }}
                                    inputProps={{ min: 0 }}
                                    sx={{ width: 150 }}
                                  />
                                </Box>
                              ))}

                              {allDaysFilled && (
                                <Button
                                  variant="outlined"
                                  onClick={() => setView("weekend")}
                                  sx={(theme) => ({
                                    position: "absolute",
                                    bottom: { xs: 10, sm: 15, md: 20 }, // ✅ responsive positioning
                                    right: { xs: 10, sm: 15, md: 20 },
                                    width: { xs: 70, sm: 90, md: 90 },
                                    color: theme.palette.text.primary + " !important", // ✅ same as h5
                                    borderColor: "green !important",
                                    "&:hover": {
                                      bgcolor: "green !important",
                                      color: theme.palette.text.primary + " !important", // ✅ stays same
                                      borderColor: "darkgreen !important",
                                    },
                                  })}
                                >
                                  {/* Next */}
                                  {t("user.hoursCalculator.next")}
                                </Button>
                              )}
                            </Card>
                          );
                        })}
                      </Box>
                    </Box>

                    <IconButton
                      onClick={() =>
                        setActiveIndex((prev) =>
                          isArabic
                            ? prev === days.length - 1 // RTL: reverse
                              ? 0
                              : prev + 1
                            : prev === 0 // LTR: normal
                              ? days.length - 1
                              : prev - 1
                        )
                      }
                      sx={{
                        position: "absolute",
                        bottom: { xs: 10, sm: 20, md: 200 },
                        left: { xs: "5%", sm: "10%", md: "30%" }, // ✅ percentage-based responsive
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(255,255,255,0.8)",
                        color: "primary.main",
                        borderRadius: "50%",
                        width: { xs: 32, sm: 40 }, // ✅ responsive size
                        height: { xs: 32, sm: 40 },
                        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                        // display: { xs: "none", sm: "none" }
                        display: device === "desktop" ? "flex" : "none",
                      }}
                    >
                      <ArrowBackIos fontSize="small" />
                    </IconButton>

                    {/* Right Arrow */}
                    <IconButton
                      onClick={() =>
                        setActiveIndex((prev) =>
                          isArabic
                            ? prev === 0 // RTL: reverse
                              ? days.length - 1
                              : prev - 1
                            : prev === days.length - 1 // LTR: normal
                              ? 0
                              : prev + 1
                        )
                      }
                      sx={{
                        position: "absolute",
                        top: "70%", // move slightly down
                        right: { xs: "5%", sm: "10%", md: "30%" }, // ✅ percentage-based responsive
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(255,255,255,0.8)", // glassy background
                        color: "primary.main", // arrow color
                        borderRadius: "50%", // circle shape
                        width: 40,
                        height: 40,
                        boxShadow: "0 4px 10px rgba(0,0,0,0.2)", // soft shadow
                        transition: "all 0.3s ease",
                        display: device === "desktop" ? "flex" : "none",
                      }}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton>

                    {/* dots navigation */}
                    <Box mt={2} display="flex" justifyContent="center" gap={1}>
                      {days.map((_, index) => (
                        <Box
                          key={index}
                          onClick={() => setActiveIndex(index)}
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: index === activeIndex ? "primary.main" : "grey.400",
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {view === "weekend" && (
                <>
                  <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
                    {weekendDays.map((day) => (
                      <Card
                        key={day}
                        sx={{
                          p: { xs: 2, sm: 3 },
                          width: { xs: "100%", sm: "48%", md: 400 }, // ✅ responsive widths
                          borderRadius: 2,
                          boxShadow: 3,
                        }}
                      >
                        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                          {/* {day} */}
                          {t(`user.hoursCalculator.weekendDays.${day}`)}
                        </Typography>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={noWorkingHours[day]}
                              onChange={() => {
                                setNoWorkingHours((prev) => ({
                                  ...prev,
                                  [day]: !prev[day], // toggle
                                }));

                                setWeekendHoursData((prev) => ({
                                  ...prev,
                                  [day]: !noWorkingHours[day]
                                    ? { technical: "0", meetings: "0", learning: "0" } // first click → 0
                                    : { technical: "", meetings: "", learning: "" }, // second click → empty
                                }));
                              }}
                            />
                          }
                          label={t("user.hoursCalculator.noWorkingHrs")}
                          sx={{ mb: 2 }}
                        />

                        {[
                          { type: "technical", label: "user.hoursCalculator.technical" },
                          { type: "meetings", label: "user.hoursCalculator.meetings" },
                          { type: "learning", label: "user.hoursCalculator.learningAndDev" },
                        ].map(({ type, label }) => (
                          <Box
                            key={type}
                            display="flex"
                            alignItems="flex-start"
                            gap={3} // 🔹 spacing between title & field
                            mb={2}
                            sx={{ pl: 1 }} // 🔹 padding left for fields section
                          >
                            {/* Field title */}
                            <Typography
                              variant="body2" // 🔹 shorter font size
                              sx={{
                                width: { xs: "100%", sm: 120, md: 160 },
                                whiteSpace: { xs: "normal", sm: "nowrap" },
                              }}
                            >
                              {t(label)}
                            </Typography>

                            {/* Input */}
                            <TextField
                              type="number"
                              size="small"
                              value={weekendHoursData[day][type]}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || Number(value) >= 0) {
                                  setWeekendHoursData((prev) => ({
                                    ...prev,
                                    [day]: { ...prev[day], [type]: value },
                                  }));
                                }
                              }}
                              // sx={{ width: 120 }}
                              sx={{ width: { xs: "100%", sm: 120, md: 160 } }} // ✅ full width on mobile
                              inputProps={{ min: 0 }}
                              disabled={noWorkingHours[day]}
                            />
                          </Box>
                        ))}
                      </Card>
                    ))}
                  </Box>
                </>
              )}

              {view === "weekend" && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection={{ xs: "column", sm: "row" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setView("work")}
                    sx={(theme) => ({
                      mt: 2,
                      color: theme.palette.text.primary + " !important",
                      borderColor: "green !important",
                      "&:hover": {
                        bgcolor: "green !important",
                        color: theme.palette.text.primary + " !important",
                        borderColor: "darkgreen !important",
                      },
                    })}
                  >
                    {t("user.hoursCalculator.back")}
                  </Button>
                  {weekendDays.every((day) =>
                    Object.values(weekendHoursData[day]).every((v) => v !== "")
                  ) && (
                    <Box display="flex" alignItems="center" gap={1} mt={3}>
                      <Typography variant="body1" sx={{ ml: 4 }}>
                        {t("user.hoursCalculator.calculate")}
                      </Typography>
                      <IconButton
                        onClick={() => setConfOpen(true)}
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          "&:hover": { bgcolor: "primary.dark" },
                        }}
                      >
                        <CalculateIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {sameWorkingHours === "yes" && (
            <>
              {sameHoursView === "work" ? (
                <Card
                  sx={{
                    p: 3,
                    boxShadow: 3,
                    borderRadius: 2,
                    width: "100%",
                    maxWidth: 450,
                    margin: "0 auto",
                    minHeight: 340,
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                    {/* Day Working Hours */}
                    {t("user.hoursCalculator.dayWorkingHrs")}
                  </Typography>

                  {[
                    {
                      type: "technical",
                      labelKey: "user.hoursCalculator.technical",
                      descKey: "user.hoursCalculator.technicalDescription",
                    },
                    {
                      type: "meetings",
                      labelKey: "user.hoursCalculator.meetings",
                      descKey: "user.hoursCalculator.meetingsDescription",
                    },
                    {
                      type: "learning",
                      labelKey: "user.hoursCalculator.learningAndDev",
                      descKey: "user.hoursCalculator.learningAndDevDescription",
                    },
                  ].map(({ type, labelKey, descKey }) => (
                    <Box key={type} display="flex" gap={4} mb={2} alignItems="flex-start">
                      {/* Label and description */}
                      <Box display="flex" flexDirection="column" sx={{ width: 180, ml: 1 }}>
                        {/* increased width to fit longer labels */}
                        <Typography
                          variant="body2"
                          sx={{
                            width: { xs: "100%", sm: 120, md: 160 },
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                          }}
                        >
                          {/* {label} */}
                          {t(labelKey)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {/* {description} */}
                          {t(descKey)}
                        </Typography>
                      </Box>

                      {/* Input field */}
                      <TextField
                        type="number"
                        size="small"
                        // placeholder="Enter hours"
                        placeholder={t("user.hoursCalculator.enterHrs")}
                        value={hoursDataAllDays[type]}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || Number(value) >= 0) {
                            setHoursDataAllDays((prev) => ({
                              ...prev,
                              [type]: value,
                            }));
                          }
                        }}
                        inputProps={{ min: 0 }}
                        sx={{ width: 150 }}
                      />
                    </Box>
                  ))}

                  {Object.values(hoursDataAllDays).every((v) => v !== "") && (
                    <Box mt={2} display="flex" justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => setSameHoursView("weekend")}
                        sx={(theme) => ({
                          position: "absolute",
                          bottom: { xs: 10, sm: 15, md: 20 }, // ✅ responsive positioning
                          right: { xs: 10, sm: 15, md: 20 },
                          width: { xs: 70, sm: 90, md: 90 },
                          color: theme.palette.text.primary + " !important", // ✅ same as h5
                          borderColor: "green !important",
                          "&:hover": {
                            bgcolor: "green !important",
                            color: theme.palette.text.primary + " !important", // ✅ stays same
                            borderColor: "darkgreen !important",
                          },
                        })}
                      >
                        {/* Next */}
                        {t("user.hoursCalculator.next")}
                      </Button>
                    </Box>
                  )}
                </Card>
              ) : (
                <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
                  {weekendDays.map((day) => (
                    <Card
                      key={day}
                      // sx={{ p: 3, minWidth: 250, borderRadius: 2, boxShadow: 3 }}
                      sx={{
                        p: { xs: 2, sm: 3 },
                        width: { xs: "100%", sm: "48%", md: 400 }, // ✅ responsive widths
                        borderRadius: 2,
                        boxShadow: 3,
                      }}
                    >
                      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                        {/* {day} */}
                        {t(`user.hoursCalculator.weekendDays.${day}`)}
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={noSameWorkingHours[day]}
                            onChange={() => {
                              setNoSameWorkingHours((prev) => ({ ...prev, [day]: !prev[day] }));
                              setSameHoursWeekendData((prev) => ({
                                ...prev,
                                [day]: !noSameWorkingHours[day]
                                  ? { technical: "0", meetings: "0", learning: "0" }
                                  : { technical: "", meetings: "", learning: "" },
                              }));
                            }}
                          />
                        }
                        // label="No Working Hours"
                        label={t("user.hoursCalculator.noWorkingHrs")}
                        sx={{ mb: 2 }}
                      />

                      {/* {["technical", "meetings", "learning"].map((type) => ( */}
                      {[
                        { type: "technical", label: "user.hoursCalculator.technical" },
                        { type: "meetings", label: "user.hoursCalculator.meetings" },
                        { type: "learning", label: "user.hoursCalculator.learningAndDev" },
                      ].map(({ type, label }) => (
                        <Box
                          key={type}
                          display="flex"
                          alignItems="flex-start"
                          gap={3}
                          mb={2}
                          sx={{ pl: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: { xs: "100%", sm: 120, md: 160 },
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                            }}
                          >
                            {/* {label} */}
                            {t(label)}
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            value={sameHoursWeekendData[day][type]}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || Number(value) >= 0) {
                                setSameHoursWeekendData((prev) => ({
                                  ...prev,
                                  [day]: { ...prev[day], [type]: value },
                                }));
                              }
                            }}
                            sx={{ width: { xs: "100%", sm: 120, md: 160 } }}
                            inputProps={{ min: 0 }}
                            disabled={noSameWorkingHours[day]}
                          />
                        </Box>
                      ))}
                    </Card>
                  ))}
                </Box>
              )}

              {sameHoursView === "weekend" && (
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSameHoursView("work")}
                    sx={(theme) => ({
                      mt: 2,
                      color: theme.palette.text.primary + " !important",
                      borderColor: "green !important",
                      "&:hover": {
                        bgcolor: "green !important",
                        color: theme.palette.text.primary + " !important",
                        borderColor: "darkgreen !important",
                      },
                    })}
                  >
                    {/* Back */}
                    {t("user.hoursCalculator.back")}
                  </Button>
                  {weekendDays.every((day) =>
                    Object.values(sameHoursWeekendData[day]).every((v) => v !== "")
                  ) && (
                    <Box display="flex" alignItems="center" gap={1} mt={3}>
                      <Typography variant="body1" sx={{ ml: 4 }}>
                        {" "}
                        {t("user.hoursCalculator.calculate")}
                      </Typography>
                      <IconButton
                        onClick={() => setConfOpen(true)}
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          "&:hover": { bgcolor: "primary.dark" },
                        }}
                      >
                        <CalculateIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          <SnackbarWrapper
            ref={snackbarRef}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          />

          <ConfirmationDialog
            open={confOpen}
            onClose={() => setConfOpen(false)}
            message="Are you sure you want to calculate and save hours?"
            onConfirm={async () => {
              try {
                await handleSaveHours();
                setConfOpen(false);
              } catch (err) {
                // snackbarRef.current?.showSnackbar("Error saving hours.", "error");
                console.error("🔥 onConfirm catch:", err);
                snackbarRef.current?.showSnackbar(
                  t("user.hoursCalculator.snackbar.error"),
                  "error"
                );
              }
            }}
          />
        </>
      ) : jobsLoading ? (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <Typography variant="body1" color="textSecondary">
          ⚠️ No jobs available.
        </Typography>
      )}
    </Box>
  );
}
